/**
 * @file clienttimezone.js
 * @description Resolves the tenant's configured time zone (Configuration →
 *   Time Settings → ClientTimezone) and exposes the helpers the dashboards
 *   need to do date math in that zone instead of the browser's.
 *
 * Everything on the dashboards used to key off the browser: getDateRange()
 * built its presets from `new Date()`, and the heatmap shifted UTC cells with
 * `getTimezoneOffset()`. That means the same range preset resolves to
 * different windows depending on where the user happens to be sitting. The
 * configured zone is the tenant's operating clock — send windows, compliance
 * hours, "today" — so it's the one the reporting should use.
 *
 * Scope: display and range selection only. Server-side send-window logic
 * already has its own per-offset handling; nothing here touches it.
 *
 * Place at src/utils/clienttimezone.js — the import path below assumes
 * services/ is a sibling of utils/.
 */
import { useEffect, useState } from "react";
import { getclientconfiguration } from "../services/api";

// ── Zone table ─────────────────────────────────────────────────────
// Keys are the codes stored in ClientConfiguration.ClientTimezone; they
// must stay in sync with the <MenuItem value="…"> list on the
// Configuration page.
export const TZ_CODE_TO_IANA = {
  UTC: "UTC",
  PT:  "America/Los_Angeles",
  MT:  "America/Denver",
  CT:  "America/Chicago",
  ET:  "America/New_York",
  AKT: "America/Anchorage",
  HST: "Pacific/Honolulu",
};

export const DEFAULT_TZ_CODE = "UTC";
export const DEFAULT_TZ      = "UTC";

export function resolveZone(code) {
  return TZ_CODE_TO_IANA[String(code || "").toUpperCase()] || DEFAULT_TZ;
}

// ── Module-level cache ─────────────────────────────────────────────
// One fetch per page load, shared by every dashboard. `subscribers` lets
// mounted hooks re-render when the config is saved, so a zone change
// doesn't need a full page reload.
let cachedZone = null;
let cachedCode = null;
let inflight   = null;
const subscribers = new Set();

function publish() {
  subscribers.forEach(fn => {
    try { fn(cachedZone, cachedCode); } catch { /* subscriber's problem */ }
  });
}

export function loadClientTimeZone({ force = false } = {}) {
  if (!force && cachedZone) return Promise.resolve(cachedZone);
  if (!force && inflight)   return inflight;

  inflight = getclientconfiguration()
    .then(cfg => {
      // Both spellings on purpose. The column is dbo.clientconfig.ClientTimeZone
      // (capital Z); SQL Server is case-insensitive about that but the JS key
      // on the returned row is not, so `cfg.ClientTimezone` is undefined unless
      // the API happens to alias it. Reading either avoids a silent fall back
      // to UTC that looks exactly like "the setting isn't working".
      const raw = cfg?.ClientTimeZone ?? cfg?.ClientTimezone;
      cachedCode = String(raw || DEFAULT_TZ_CODE).toUpperCase();
      cachedZone = resolveZone(cachedCode);
      return cachedZone;
    })
    .catch(() => {
      // Config unreachable — fall back to UTC rather than silently
      // reverting to whatever zone the browser happens to be in.
      cachedCode = DEFAULT_TZ_CODE;
      cachedZone = DEFAULT_TZ;
      return cachedZone;
    })
    .then(z => { publish(); return z; })
    .finally(() => { inflight = null; });

  return inflight;
}

/** Re-read the config after a save on the Configuration page. */
export function refreshClientTimeZone() {
  return loadClientTimeZone({ force: true });
}

/** Last resolved zone. UTC until the first fetch lands. */
export function getClientTimeZone() { return cachedZone || DEFAULT_TZ; }
export function getClientTimeZoneCode() { return cachedCode || DEFAULT_TZ_CODE; }

// ── Intl plumbing ──────────────────────────────────────────────────
const fmtCache = new Map();

function partsFormatter(timeZone) {
  if (!fmtCache.has(timeZone)) {
    let f;
    try {
      f = new Intl.DateTimeFormat("en-US", {
        timeZone, hourCycle: "h23",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    } catch {
      // Unknown zone string — degrade to UTC instead of throwing into
      // the render path.
      f = new Intl.DateTimeFormat("en-US", {
        timeZone: "UTC", hourCycle: "h23",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    }
    fmtCache.set(timeZone, f);
  }
  return fmtCache.get(timeZone);
}

/**
 * Minutes the zone lags UTC at `date`, matching the sign convention of
 * Date.getTimezoneOffset() (EDT → 240, UTC → 0, HST → 600).
 *
 * Computed per-instant via Intl, so DST is correct for the date passed
 * rather than for "now".
 */
export function zoneOffsetMinutes(date = new Date(), timeZone = getClientTimeZone()) {
  try {
    const parts = partsFormatter(timeZone).formatToParts(date);
    const get   = (t) => Number(parts.find(p => p.type === t)?.value);
    const asUTC = Date.UTC(
      get("year"), get("month") - 1, get("day"),
      get("hour"), get("minute"), get("second"),
    );
    return -Math.round((asUTC - date.getTime()) / 60000);
  } catch {
    return 0;
  }
}

/**
 * A Date whose LOCAL field accessors (getFullYear/getMonth/getDate/getDay)
 * read as the wall clock in `timeZone`. Drop-in replacement for
 * `new Date()` inside getDateRange() — all the existing getDate()/setDate()
 * arithmetic and the toLocalYMD() serializer keep working unchanged.
 *
 * The returned Date is NOT a valid instant; use it for calendar math only,
 * never for comparison against real timestamps.
 */
export function zonedNow(timeZone = getClientTimeZone()) {
  const now     = new Date();
  const zoneOff = zoneOffsetMinutes(now, timeZone);

  // getTimezoneOffset() is read at `now`, but the shifted instant may land on
  // the other side of a DST transition in the BROWSER's zone — in which case
  // its local fields read an hour off, which can roll the date near midnight.
  // Re-solve once against the shifted instant's own offset (a fixed point:
  // one iteration is always enough, offsets don't change twice in a day).
  const first = new Date(now.getTime() + (now.getTimezoneOffset() - zoneOff) * 60000);
  return new Date(now.getTime() + (first.getTimezoneOffset() - zoneOff) * 60000);
}

/**
 * Shift a (UTC day-of-week, UTC hour) pair into the configured zone.
 * day_of_week convention: 1=Mon … 7=Sun (matches SQL DATEFIRST=1).
 *
 * Replaces the old utcToLocal(). Same caveat as before: heatmap rows are
 * pre-aggregated with no date left to convert against, so one offset is
 * applied to every cell — ranges spanning a DST change (90D, 1Y) run an
 * hour off on the older portion.
 */
export function utcToZone(dayOfWeek, hourOfDay, timeZone = getClientTimeZone()) {
  const offsetMins = zoneOffsetMinutes(new Date(), timeZone);
  let totalMins = hourOfDay * 60 - offsetMins;
  let dayShift  = 0;
  while (totalMins < 0)     { totalMins += 1440; dayShift -= 1; }
  while (totalMins >= 1440) { totalMins -= 1440; dayShift += 1; }
  return {
    day:  ((dayOfWeek - 1 + dayShift) % 7 + 7) % 7 + 1,
    hour: Math.floor(totalMins / 60),
  };
}

/**
 * Format an API timestamp in the configured zone.
 *
 * Replaces formatDatetoLocal() on any screen that shows a wall-clock time
 * (schedules, upcoming messages). That helper renders in whatever zone the
 * browser is in, so a 9:00 AM PT send reads as 12:00 PM to anyone on the
 * east coast.
 *
 * `assumeUtc` handles naive strings — "2026-08-03T14:30:00" or
 * "2026-08-03 14:30:00.000" with no Z and no offset. JS parses the ISO form
 * as browser-local, which is wrong for values that came out of a UTC-storing
 * column. Default is to treat them as UTC. Flip it to false if any endpoint
 * on this page returns strings already localized server-side.
 *
 * Options:
 *   withZone  — append the abbreviation ("08/03/2026, 2:30 PM PDT")
 *   dateOnly  — drop the time portion
 */
export function formatDateInZone(
  value,
  timeZone = getClientTimeZone(),
  { withZone = false, dateOnly = false, assumeUtc = true } = {},
) {
  if (value == null || value === "") return "";

  let d;
  if (value instanceof Date) {
    d = value;
  } else {
    const s = String(value).trim();

    // Date-only value ("2026-08-03") — a calendar date has no zone, so there
    // is nothing to convert. Converting anyway would push it back a day in
    // every negative-offset zone. Format the parts straight through.
    const dateOnly_ = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly_) return `${dateOnly_[2]}/${dateOnly_[3]}/${dateOnly_[1]}`;

    const naive = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?)?$/.test(s);
    d = new Date(assumeUtc && naive ? `${s.replace(" ", "T")}Z` : s);
  }
  if (isNaN(d)) return String(value);

  const opts = {
    timeZone,
    month: "2-digit", day: "2-digit", year: "numeric",
    ...(dateOnly ? {} : { hour: "numeric", minute: "2-digit", hour12: true }),
    ...(withZone ? { timeZoneName: "short" } : {}),
  };

  try {
    return new Intl.DateTimeFormat("en-US", opts).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "UTC" }).format(d);
  }
}

/** Current abbreviation for the zone — "PDT", "EST", "HST", "UTC". */
export function zoneAbbr(timeZone = getClientTimeZone(), date = new Date()) {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
      .formatToParts(date)
      .find(p => p.type === "timeZoneName");
    return part ? part.value : timeZone;
  } catch {
    return timeZone;
  }
}

// ── Hook ───────────────────────────────────────────────────────────
/**
 * Returns { timeZone, tzCode, tzReady }.
 *
 * Gate the first data load on `tzReady` — firing before the config lands
 * would request a UTC-derived window and then immediately refetch when the
 * real zone arrives.
 */
export function useClientTimeZone() {
  const [state, setState] = useState(() => ({
    timeZone: getClientTimeZone(),
    tzCode:   getClientTimeZoneCode(),
    tzReady:  Boolean(cachedZone),
  }));

  useEffect(() => {
    let alive = true;
    const onChange = (zone, code) => {
      if (alive) setState({ timeZone: zone, tzCode: code, tzReady: true });
    };
    subscribers.add(onChange);
    loadClientTimeZone().then(zone => onChange(zone, getClientTimeZoneCode()));
    return () => { alive = false; subscribers.delete(onChange); };
  }, []);

  return state;
}